import { useContext, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AppHeader } from '@/components/shared/AppHeader';
import { DocumentosProvider } from '../DocumentosProvider';
import { DocumentosContext } from '@/contexts/DocumentosContext';
import { ESTADO_MODELOS_INICIAL, PAGINACAO_MODELOS_INICIAL } from '@/lib/utils/helpers';
import {
  exibeSatNoPeriodo,
  modeloVisivelNasEntradas,
  modeloVisivelNasSaidas,
  paginarDocumentos,
} from './documentos-helpers';
import { TotaisCards } from './TotaisCards';
import { SituacoesDoModelo } from './SituacoesDoModelo';
import { calcularTotaisDoTopo } from './totais-do-topo';
import { DocumentosTable } from './DocumentosTable';
import { DocumentosTableSkeleton } from './DocumentosTableSkeleton';
import { DetalhesNotaModal } from './DetalhesNotaModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';
import { FiltrosModal } from './FiltrosModal';
import { ExportarModal } from './ExportarModal';
import { BuscaEFinalidade } from './BuscaEFinalidade';
import { AvisoRecebimento } from './AvisoRecebimento';
import { LoadingDots } from './LoadingDots';
import { SeloEmalote } from './SeloEmalote';
import { FileText, Filter, ChevronDown, Download } from 'lucide-react';
import type { Documento } from '@/services/Documentos';
import {
  useContarCteEmalote,
  useContarNfcEmalote,
  useContarNfeEmalote,
} from '@/services/Documentos';
import { formatQuantidade } from '@/lib/utils/formatters';

function formatYmd(year: number, month: number, day: number) {
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

function getMonthRange(dateStr: string) {
  const [y, m] = dateStr.split('-').map(Number);
  if (!y || !m) return null;
  const firstDay = new Date(y, m - 1, 1);
  const lastDay = new Date(y, m, 0);
  return {
    DataInicio: formatYmd(firstDay.getFullYear(), firstDay.getMonth() + 1, 1),
    DataFim: formatYmd(lastDay.getFullYear(), lastDay.getMonth() + 1, lastDay.getDate()),
  };
}

function ordenarModelosComNfagPorUltimo<T extends { key: string }>(modelos: T[]) {
  return [...modelos].sort((modeloAtual, proximoModelo) => {
    if (modeloAtual.key === 'nfag') return 1;
    if (proximoModelo.key === 'nfag') return -1;
    return 0;
  });
}

function isModeloEmBreve(modeloKey: string) {
  return modeloKey === 'nfag';
}

export function DocumentosPage() {
  return (
    <DocumentosProvider>
      <DocumentosPageInner />
    </DocumentosProvider>
  );
}

function DocumentosPageInner() {
  const ctx = useContext(DocumentosContext);
  if (!ctx) {
    throw new Error('DocumentosPageInner deve ser usado dentro de <DocumentosProvider>');
  }

  const {
    tipoDocumento,
    setTipoDocumento,
    filtros,
    filtrosHeaderAplicados,
    handleBuscar,
    handleLimparFiltrosGlobais,
    handleAplicarFiltros,
    dadosEntradas,
    dadosSaidas,
    aplicacao,
  } = ctx;

  const exibirSatSaidas = useMemo(
    () => exibeSatNoPeriodo(filtrosHeaderAplicados.dataFim),
    [filtrosHeaderAplicados.dataFim]
  );

  const documentosPorModeloEntradasVisiveis = useMemo(() => {
    const modelosFiltrados = (dadosEntradas.documentosPorModelo ?? []).filter(modelo =>
      modeloVisivelNasEntradas(modelo.key)
    );

    return ordenarModelosComNfagPorUltimo(modelosFiltrados);
  }, [dadosEntradas.documentosPorModelo]);

  const totalEntradasTab = useMemo(() => {
    return documentosPorModeloEntradasVisiveis.reduce((acc, modelo) => {
      const totalModelo =
        typeof modelo.total === 'number' ? modelo.total : modelo.documentos.length;
      return acc + totalModelo;
    }, 0);
  }, [documentosPorModeloEntradasVisiveis]);

  const documentosPorModeloSaidasVisiveis = useMemo(() => {
    const modelosFiltrados = (dadosSaidas.documentosPorModelo ?? []).filter(modelo =>
      modeloVisivelNasSaidas(modelo.key, exibirSatSaidas)
    );

    return ordenarModelosComNfagPorUltimo(modelosFiltrados);
  }, [dadosSaidas.documentosPorModelo, exibirSatSaidas]);

  const totalSaidasTab = useMemo(() => {
    return documentosPorModeloSaidasVisiveis.reduce((acc, modelo) => {
      const totalModelo =
        typeof modelo.total === 'number' ? modelo.total : modelo.documentos.length;
      return acc + totalModelo;
    }, 0);
  }, [documentosPorModeloSaidasVisiveis]);

  const totaisDoTopo = useMemo(
    () =>
      calcularTotaisDoTopo(
        {
          modelos: documentosPorModeloEntradasVisiveis,
          carregando:
            dadosEntradas.isFetching || (dadosEntradas.isPending && !dadosEntradas.isError),
          erro: dadosEntradas.isError,
        },
        {
          modelos: documentosPorModeloSaidasVisiveis,
          carregando: dadosSaidas.isFetching || (dadosSaidas.isPending && !dadosSaidas.isError),
          erro: dadosSaidas.isError,
        }
      ),
    [
      documentosPorModeloEntradasVisiveis,
      documentosPorModeloSaidasVisiveis,
      dadosEntradas.isFetching,
      dadosEntradas.isPending,
      dadosEntradas.isError,
      dadosSaidas.isFetching,
      dadosSaidas.isPending,
      dadosSaidas.isError,
    ]
  );

  const periodoInformado = !!filtrosHeaderAplicados.dataInicio && !!filtrosHeaderAplicados.dataFim;
  const rangeMes = useMemo(() => {
    if (!periodoInformado) return null;
    const inicio = getMonthRange(filtrosHeaderAplicados.dataInicio as string);
    const fim = getMonthRange(filtrosHeaderAplicados.dataFim as string);
    if (!inicio || !fim) return null;
    return { DataInicio: inicio.DataInicio, DataFim: fim.DataFim };
  }, [periodoInformado, filtrosHeaderAplicados.dataInicio, filtrosHeaderAplicados.dataFim]);

  const cnpjContribuinte = filtrosHeaderAplicados.empresaId || '';

  const nfeEntradaEmalote = useContarNfeEmalote(
    rangeMes && cnpjContribuinte
      ? {
          CNPJ_Contribuinte: cnpjContribuinte,
          Ind_Oper: '0',
          DataInicio: rangeMes.DataInicio,
          DataFim: rangeMes.DataFim,
        }
      : undefined,
    { enabled: periodoInformado && !!cnpjContribuinte }
  );

  const cteEntradaEmalote = useContarCteEmalote(
    rangeMes && cnpjContribuinte
      ? {
          CNPJ_Contribuinte: cnpjContribuinte,
          DataInicio: rangeMes.DataInicio,
          DataFim: rangeMes.DataFim,
        }
      : undefined,
    { enabled: periodoInformado && !!cnpjContribuinte }
  );

  const nfcSaidaEmalote = useContarNfcEmalote(
    rangeMes && cnpjContribuinte
      ? {
          CNPJ_Contribuinte: cnpjContribuinte,
          DataInicio: rangeMes.DataInicio,
          DataFim: rangeMes.DataFim,
        }
      : undefined,
    { enabled: periodoInformado && !!cnpjContribuinte }
  );

  const nfeSaidaEmalote = useContarNfeEmalote(
    rangeMes && cnpjContribuinte
      ? {
          CNPJ_Contribuinte: cnpjContribuinte,
          Ind_Oper: '1',
          DataInicio: rangeMes.DataInicio,
          DataFim: rangeMes.DataFim,
        }
      : undefined,
    { enabled: periodoInformado && !!cnpjContribuinte }
  );

  const [modelosExpandidos, setModelosExpandidos] =
    useState<Record<string, boolean>>(ESTADO_MODELOS_INICIAL);
  const [paginacaoModelos, setPaginacaoModelos] =
    useState<Record<string, { pagina: number; itensPorPagina: number }>>(PAGINACAO_MODELOS_INICIAL);

  const isLoading = dadosEntradas.isLoading || dadosSaidas.isLoading;

  const [showFilters, setShowFilters] = useState(false);
  const [showDetalhesModal, setShowDetalhesModal] = useState(false);
  const [showExportarModal, setShowExportarModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<Documento | null>(null);

  const handlePaginaChange = (modeloKey: string, novaPagina: number) => {
    setPaginacaoModelos(prev => ({
      ...prev,
      [modeloKey]: { ...prev[modeloKey], pagina: novaPagina },
    }));
  };

  const handleItensPorPaginaChange = (modeloKey: string, novosItens: number) => {
    setPaginacaoModelos(prev => ({
      ...prev,
      [modeloKey]: { pagina: 1, itensPorPagina: novosItens },
    }));
  };

  const toggleModelo = (modeloKey: string) => {
    setModelosExpandidos(prev => ({
      ...prev,
      [modeloKey]: !prev[modeloKey],
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader
        title="Documentos Fiscais"
        subtitle="NF-e, NFC-e, CT-e"
        onBuscar={handleBuscar}
        onLimparFiltros={handleLimparFiltrosGlobais}
      />

      <main className="w-full px-6 py-8">
        <motion.div
          className="mb-8 flex items-center justify-between"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <div>
            <h2 className="text-3xl font-bold text-foreground text-balance mb-2">
              Documentos Fiscais
            </h2>
            <p className="text-muted-foreground text-pretty">
              Visualize e gerencie todos os documentos fiscais com tags da Lei 214/2025
            </p>
          </div>
          <motion.div
            className="flex gap-2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1, ease: 'easeOut' }}
          >
            <Button variant="outline" onClick={() => setShowFilters(true)} disabled={isLoading}>
              <Filter className="h-4 w-4 mr-2" />
              Filtros
            </Button>
            <FiltrosModal
              open={showFilters}
              onOpenChange={setShowFilters}
              initialFiltro={filtros}
              tipoDocumento={tipoDocumento}
              exibirSat={exibirSatSaidas}
              onAplicar={handleAplicarFiltros}
            />
            <Button
              variant="outline"
              onClick={() => setShowExportarModal(true)}
              disabled={isLoading}
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </Button>
          </motion.div>
        </motion.div>

        <div className="mb-6">
          <BuscaEFinalidade
            busca={filtros.busca}
            finalidade={filtros.finalidade}
            desabilitado={isLoading}
            onAplicar={handleAplicarFiltros}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2, ease: 'easeOut' }}
        >
          <TotaisCards totais={totaisDoTopo} />
        </motion.div>

        <Tabs
          defaultValue="entradas"
          value={tipoDocumento === '0' ? 'entradas' : 'saidas'}
          onValueChange={value => setTipoDocumento(value === 'entradas' ? '0' : '1')}
          className="space-y-6"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="entradas">
              Entradas (
              {dadosEntradas.isLoading || dadosEntradas.isFetching ? (
                <LoadingDots />
              ) : (
                totalEntradasTab
              )}
              )
            </TabsTrigger>
            <TabsTrigger
              value="saidas"
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
            >
              Saídas (
              {dadosSaidas.isLoading || dadosSaidas.isFetching ? <LoadingDots /> : totalSaidasTab})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="entradas" className="space-y-6">
            <AvisoRecebimento
              cnpjs={filtrosHeaderAplicados.empresaCnpjs ?? filtrosHeaderAplicados.empresaId}
              dataInicio={filtrosHeaderAplicados.dataInicio}
              dataFim={filtrosHeaderAplicados.dataFim}
              aplicacao={aplicacao}
            />
            {documentosPorModeloEntradasVisiveis.map((modelo, index) => {
              const paginacao = paginacaoModelos[modelo.key] || { pagina: 1, itensPorPagina: 10 };
              const documentosPaginados = paginarDocumentos(
                modelo.documentos,
                paginacao.pagina,
                paginacao.itensPorPagina
              );
              const isExpanded = modelosExpandidos[modelo.key];
              const isEmBreve = isModeloEmBreve(modelo.key);

              return (
                <motion.div
                  key={modelo.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05, ease: 'easeOut' }}
                >
                  <Collapsible
                    open={isEmBreve ? false : isExpanded}
                    onOpenChange={() => {
                      if (isEmBreve) return;
                      toggleModelo(modelo.key);
                    }}
                  >
                    <div className="relative">
                      {isEmBreve ? (
                        <Badge className="pointer-events-none absolute left-4 top-0 z-10 -translate-y-1/2 rounded-full bg-red-500 px-3 py-1 text-white shadow-md hover:bg-red-500">
                          Em breve
                        </Badge>
                      ) : null}
                      <Card>
                        <CollapsibleTrigger asChild>
                          <CardHeader className={isEmBreve ? 'cursor-default' : 'cursor-pointer'}>
                            <CardTitle className="flex items-center gap-2 justify-between">
                              <div className="flex items-center gap-2">
                                <FileText className="h-5 w-5 text-accent" />
                                {modelo.nome}
                                <span className="inline-flex items-center gap-2">
                                  <Badge
                                    variant="secondary"
                                    className="bg-accent text-accent-foreground"
                                  >
                                    {dadosEntradas.isLoading ? (
                                      <LoadingDots className="flex items-center gap-0.5" />
                                    ) : (
                                      <span>{formatQuantidade(modelo.total)} documento(s)</span>
                                    )}
                                  </Badge>
                                  <SituacoesDoModelo
                                    situacoes={modelo.situacoes}
                                    carregando={dadosEntradas.isLoading}
                                  />
                                  {periodoInformado &&
                                  !!cnpjContribuinte &&
                                  !!rangeMes &&
                                  (modelo.key === 'nfe' || modelo.key === 'cte') ? (
                                    <SeloEmalote
                                      contagem={
                                        modelo.key === 'nfe' ? nfeEntradaEmalote : cteEntradaEmalote
                                      }
                                      periodo={rangeMes}
                                      sentido="entrada"
                                    />
                                  ) : null}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className="bg-green-50 text-green-700 border-green-300"
                                >
                                  Crédito
                                </Badge>
                                <div
                                  className={
                                    isEmBreve
                                      ? 'p-1 rounded-full opacity-40'
                                      : 'p-1 rounded-full hover:bg-muted transition-colors duration-200'
                                  }
                                >
                                  <ChevronDown
                                    className={`h-5 w-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                  />
                                </div>
                              </div>
                            </CardTitle>
                          </CardHeader>
                        </CollapsibleTrigger>
                        <AnimatePresence initial={false}>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: 'easeInOut' }}
                              style={{ overflow: 'hidden' }}
                            >
                              <CardContent className="pt-0">
                                {dadosEntradas.isLoading ? (
                                  <DocumentosTableSkeleton tipo="entradas" />
                                ) : modelo.documentos.length > 0 ? (
                                  <DocumentosTable
                                    documentos={documentosPaginados}
                                    tipo="entradas"
                                    totalItens={modelo.documentos.length}
                                    paginaAtual={paginacao.pagina}
                                    itensPorPagina={paginacao.itensPorPagina}
                                    onPaginaChange={novaPagina =>
                                      handlePaginaChange(modelo.key, novaPagina)
                                    }
                                    onItensPorPaginaChange={novosItens =>
                                      handleItensPorPaginaChange(modelo.key, novosItens)
                                    }
                                    onViewDocument={doc => {
                                      setSelectedDoc(doc);
                                      setShowDetalhesModal(true);
                                    }}
                                  />
                                ) : (
                                  <div className="text-center py-8 text-muted-foreground">
                                    Não existem notas disponíveis para este modelo
                                  </div>
                                )}
                              </CardContent>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Card>
                    </div>
                  </Collapsible>
                </motion.div>
              );
            })}
          </TabsContent>

          <TabsContent value="saidas" className="space-y-6">
            {documentosPorModeloSaidasVisiveis.map((modelo, index) => {
              const paginacao = paginacaoModelos[modelo.key] || { pagina: 1, itensPorPagina: 10 };
              const documentosPaginados = paginarDocumentos(
                modelo.documentos,
                paginacao.pagina,
                paginacao.itensPorPagina
              );
              const isExpanded = modelosExpandidos[modelo.key];
              const isEmBreve = isModeloEmBreve(modelo.key);

              return (
                <motion.div
                  key={modelo.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05, ease: 'easeOut' }}
                >
                  <Collapsible
                    open={isEmBreve ? false : isExpanded}
                    onOpenChange={() => {
                      if (isEmBreve) return;
                      toggleModelo(modelo.key);
                    }}
                  >
                    <div className="relative">
                      {isEmBreve ? (
                        <Badge className="pointer-events-none absolute left-4 top-0 z-10 -translate-y-1/2 rounded-full bg-red-500 px-3 py-1 text-white shadow-md hover:bg-red-500">
                          Em breve
                        </Badge>
                      ) : null}
                      <Card>
                        <CollapsibleTrigger asChild>
                          <CardHeader className={isEmBreve ? 'cursor-default' : 'cursor-pointer'}>
                            <CardTitle className="flex items-center gap-2 justify-between">
                              <div className="flex items-center gap-2">
                                <FileText className="h-5 w-5 text-red-600" />
                                {modelo.nome}
                                <span className="inline-flex items-center gap-2">
                                  <Badge variant="secondary">
                                    {dadosSaidas.isLoading ? (
                                      <LoadingDots className="flex items-center gap-0.5" />
                                    ) : (
                                      <span>{formatQuantidade(modelo.total)} documento(s)</span>
                                    )}
                                  </Badge>
                                  <SituacoesDoModelo
                                    situacoes={modelo.situacoes}
                                    carregando={dadosSaidas.isLoading}
                                  />
                                  {periodoInformado &&
                                  !!cnpjContribuinte &&
                                  !!rangeMes &&
                                  (modelo.key === 'nfe' || modelo.key === 'nfc') ? (
                                    <SeloEmalote
                                      contagem={
                                        modelo.key === 'nfe' ? nfeSaidaEmalote : nfcSaidaEmalote
                                      }
                                      periodo={rangeMes}
                                      sentido="saida"
                                    />
                                  ) : null}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className="bg-red-50 text-red-700 border-red-300"
                                >
                                  Débito
                                </Badge>
                                <div
                                  className={
                                    isEmBreve
                                      ? 'p-1 rounded-full opacity-40'
                                      : 'p-1 rounded-full hover:bg-muted transition-colors duration-200'
                                  }
                                >
                                  <ChevronDown
                                    className={`h-5 w-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                  />
                                </div>
                              </div>
                            </CardTitle>
                          </CardHeader>
                        </CollapsibleTrigger>
                        <AnimatePresence initial={false}>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: 'easeInOut' }}
                              style={{ overflow: 'hidden' }}
                            >
                              <CardContent className="pt-0">
                                {dadosSaidas.isLoading ? (
                                  <DocumentosTableSkeleton tipo="saidas" />
                                ) : modelo.documentos.length > 0 ? (
                                  <DocumentosTable
                                    documentos={documentosPaginados}
                                    tipo="saidas"
                                    totalItens={modelo.documentos.length}
                                    paginaAtual={paginacao.pagina}
                                    itensPorPagina={paginacao.itensPorPagina}
                                    onPaginaChange={novaPagina =>
                                      handlePaginaChange(modelo.key, novaPagina)
                                    }
                                    onItensPorPaginaChange={novosItens =>
                                      handleItensPorPaginaChange(modelo.key, novosItens)
                                    }
                                    onViewDocument={doc => {
                                      setSelectedDoc(doc);
                                      setShowDetalhesModal(true);
                                    }}
                                  />
                                ) : (
                                  <div className="text-center py-8 text-muted-foreground">
                                    Não existem notas disponíveis para este modelo
                                  </div>
                                )}
                              </CardContent>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Card>
                    </div>
                  </Collapsible>
                </motion.div>
              );
            })}
          </TabsContent>
        </Tabs>

        <ExportarModal
          open={showExportarModal}
          onOpenChange={setShowExportarModal}
          tipoDocumento={tipoDocumento}
        />

        <DetalhesNotaModal
          open={showDetalhesModal}
          onOpenChange={setShowDetalhesModal}
          documento={selectedDoc}
          tipo={tipoDocumento}
        />
      </main>
    </div>
  );
}
